import { useEffect, useRef, useState } from 'react';
import { IUser } from '../../Party';
import { IParticipantForUser, TUserId } from '../../PartyClientResponse';
import firebase from 'firebase/app';
import _ from 'lodash';
import MatterAttractors from 'matter-attractors';
import useResizeObserver from 'use-resize-observer';
// @ts-ignore
window.decomp = require('poly-decomp');
// eslint-disable-next-line
import Matter, { Engine, Body, World, Bodies, Events } from 'matter-js';

Matter.use(MatterAttractors);

const POSITION_UPDATE_INTERVAL = 100;
const PARTICIPANT_CIRCLE_RADIUS_RATIO = 1 / 4;
const SELF_CIRCLE_RADIUS_RATIO = PARTICIPANT_CIRCLE_RADIUS_RATIO;

const offsets = [
  { x: 1, y: 1 },
  { x: -1, y: -1 },
  { x: 1, y: -1 },
  { x: -1, y: 1 },
];

function random(min: number, max: number, intResult?: boolean) {
  if (typeof min !== 'number' && typeof max !== 'number') {
    min = 0;
    max = 1;
  }

  if (typeof max !== 'number') {
    max = min;
    min = 0;
  }

  let result = min + Math.random() * (max - min);

  if (intResult) {
    result = parseInt(String(result), 10);
  }

  return result;
}

const getCirclesFromParticipants = (
  width: number,
  height: number,
  p: IParticipantForUser,
  i: number,
  user: IUser
) => {
  const offset = offsets[i % 4];

  let body: Body | null = null;
  if (user.uid === p.uid) {
    body = Bodies.circle(width / 2, height / 2, height * SELF_CIRCLE_RADIUS_RATIO, {
      isStatic: true,
      mass: 1000,
      plugin: {
        attractors: [
          function (bodyA: any, bodyB: any) {
            return {
              x: (bodyA.position.x - bodyB.position.x) * 1e-6,
              y: (bodyA.position.y - bodyB.position.y) * 1e-6,
            };
          },
        ],
      },
    });
  } else {
    const maxRadius = Math.min(height, width) * PARTICIPANT_CIRCLE_RADIUS_RATIO;
    // const radius = random(0.8 * maxRadius, maxRadius);
    const radius = maxRadius;
    body = Bodies.circle(
      width / 2 + (offset.x * width) / 4,
      height / 2 + (offset.y * height) / 4,
      radius,
      {
        // frictionStatic: 0,
        density: 0.000008,
        frictionAir: 0.006,
        restitution: 0.1,
        friction: 0.001,
      }
    );
  }
  body.label = p.uid;
  return body;
};

const useParticipantPositioning = (
  participants: IParticipantForUser[],
  user: (IUser & firebase.User) | null,
  setSizing: ({
    width,
    height,
    offsetLeft,
    offsetTop,
  }: {
    width: number;
    height: number;
    offsetTop: number;
    offsetLeft: number;
  }) => void,
  enabled: boolean
) => {
  const ref = useRef<HTMLDivElement>(null);
  const { width = 0, height = 0 } = useResizeObserver<HTMLDivElement>({ ref });
  const matterRef = useRef<HTMLDivElement>(null);
  const [engineRef, setEngine] = useState<Engine | null>(null);
  const [positionMap, setPositionMap] = useState<Partial<{ [key in TUserId]: Matter.Body }>>({});

  useEffect(() => {
    setSizing({
      width,
      height,
      offsetLeft: ref.current?.offsetLeft || 0,
      offsetTop: ref.current?.offsetTop || 0,
    });
  }, [width, height, setSizing]);

  useEffect(() => {
    if (enabled && width && height && engineRef && engineRef.world) {
      // Walls
      const WALL_FRICTION = 0.01;
      const walls = [
        // Bodies.fromVertices(width / 2, 0, [[{ x: 0, y: 0 }, { x: width, y: 0 }, { x: width / 2, y: 20 }, { x: 0, y: 0 }]], {
        //   isStatic: true,
        //   friction: WALL_FRICTION,
        // }),
        Bodies.rectangle(width / 2, 0, width, 40, {
          isStatic: true,
          friction: WALL_FRICTION,
        }),
        // Bodies.fromVertices(width / 2, height, [[{ x: 0, y: 0 }, { x: width, y: 0 }, { x: width / 2, y: -20 }, { x: 0, y: 0 }]], {
        //   isStatic: true,
        //   friction: WALL_FRICTION,
        // }),
        Bodies.rectangle(width / 2, height, width, 40, {
          isStatic: true,
          friction: WALL_FRICTION,
        }),
        Bodies.rectangle(0, height / 2, 1, height, {
          isStatic: true,
          friction: WALL_FRICTION,
        }),
        Bodies.rectangle(width, height / 2, 1, height, {
          isStatic: true,
          friction: WALL_FRICTION,
        }),
        Bodies.circle(width / 2, height / 2, 10, {
          isStatic: true,
          mass: 1000,
          plugin: {
            attractors: [
              function (bodyA: any, bodyB: any) {
                return {
                  x: (bodyA.position.x - bodyB.position.x) * 1e-6,
                  y: (bodyA.position.y - bodyB.position.y) * 1e-6,
                };
              },
            ],
          },
        }),
      ];

      World.add(engineRef.world, walls);

      return () => {
        World.clear(engineRef.world, false);
        Engine.clear(engineRef);
      };
    }
  }, [matterRef, height, width, setPositionMap, engineRef, enabled]);

  useEffect(() => {
    const engine = Engine.create({
      enableSleeping: true,
    });
    engine.world.gravity.scale = 0;

    const updater = _.debounce((event) => {
      const bodies = engine.world.bodies.reduce(
        (acc, b) => ({
          ...acc,
          [b.label as TUserId]: b,
        }),
        {}
      );
      setPositionMap(bodies);
    }, POSITION_UPDATE_INTERVAL);

    Events.on(engine, 'afterUpdate', updater);

    Engine.run(engine);
    let timeout: NodeJS.Timeout;
    const run = () => {
      Engine.update(engine, POSITION_UPDATE_INTERVAL);
      setTimeout(run, POSITION_UPDATE_INTERVAL);
    };
    run();

    setEngine(engine);

    return () => {
      clearTimeout(timeout);
      Events.off(engine, 'afterUpdate', updater);
    };
  }, [setPositionMap, setEngine]);

  // Keep circles, participants, and Matter in sync
  useEffect(() => {
    // wait for walls to be added before adding participants
    if (engineRef && engineRef.world.bodies.length && user) {
      const bodies: Partial<{ [key in TUserId]: Body }> = engineRef.world.bodies.reduce(
        (acc, b) => ({
          ...acc,
          [b.label as TUserId]: b,
        }),
        {}
      );

      const newCirclesMap: Partial<{ [key in TUserId]: Body }> = {};
      const newBodies: Body[] = [];

      (participants || [])
        .filter((p) => p?.uid !== user?.uid)
        .forEach((p, i) => {
          let body = bodies[p.uid];
          if (!body) {
            body = getCirclesFromParticipants(width, height, p, i, user);
            newBodies.push(body);
          }
          newCirclesMap[p.uid] = body;
        });

      // Clean up bodies in Matter
      const NON_PARTICIPANT_BODY_LABELS = [
        'Rectangle Body',
        'Circle Body',
        'Polygon Body',
        'Trapezoid Body',
      ];
      Object.values(bodies).forEach((b) => {
        if (!b) {
          return;
        }

        // Ignore, this is not a participant
        if (NON_PARTICIPANT_BODY_LABELS.includes(b.label)) {
          return;
        }

        // Doesn't exist anymore, remove
        if (!participants.find((part) => part.uid === b.label)) {
          World.remove(engineRef.world, b);
        }
      });

      World.add(engineRef.world, newBodies);
      setPositionMap(newCirclesMap);
    }
  }, [participants, width, height, engineRef, user]);

  return {
    ref,
    positionMap,
  };
};

export default useParticipantPositioning;
